import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { 
  Wallet, 
  Home, 
  CreditCard, 
  Sparkles, 
  PiggyBank, 
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export default function SmartBudgetPanel({ className = "" }) {
  const [budget, setBudget] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    try {
      const response = await axios.get(`${API}/budget/smart-allocation`, {
        withCredentials: true
      });
      setBudget(response.data);
    } catch (error) {
      console.error("Error fetching budget:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-slate-800/50 rounded-xl p-4 animate-pulse ${className}`}>
        <div className="h-4 bg-slate-700 rounded w-1/3 mb-2"></div>
        <div className="h-8 bg-slate-700 rounded w-1/2"></div>
      </div>
    );
  }

  if (!budget) return null;

  const { allocation, recommendations, health_status } = budget;
  const hasIncome = allocation.monthly_income > 0;

  // Status styling
  const statusStyles = {
    good: { bg: "bg-emerald-500/10", text: "text-emerald-400", icon: CheckCircle, label: "Healthy Budget" },
    tight: { bg: "bg-amber-500/10", text: "text-amber-400", icon: AlertTriangle, label: "Budget Tight" },
    critical: { bg: "bg-red-500/10", text: "text-red-400", icon: AlertTriangle, label: "Needs Attention" }
  };

  const status = statusStyles[health_status] || statusStyles.good;
  const StatusIcon = status.icon;

  // If no income, show prompt
  if (!hasIncome) {
    return (
      <div className={`bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-2xl border border-slate-700 p-5 ${className}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Smart Budget</h3>
            <p className="text-xs text-slate-400">Add your income to unlock</p>
          </div>
        </div>
        
        <p className="text-sm text-slate-300 mb-4">
          Once you add your income, we&apos;ll automatically create a personalized budget plan based on your bills and goals.
        </p>
        
        <Button 
          onClick={() => navigate("/add")}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        >
          Add Income
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  // Calculate percentages
  const essentialsPercent = (allocation.essentials / allocation.monthly_income * 100).toFixed(0);
  const debtPercent = (allocation.debt_payments / allocation.monthly_income * 100).toFixed(0);
  const lifestylePercent = (allocation.lifestyle / allocation.monthly_income * 100).toFixed(0);
  const remainingPercent = (allocation.remaining / allocation.monthly_income * 100).toFixed(0);

  return (
    <div className={`bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden ${className}`}>
      {/* Header - Always visible */}
      <div 
        className="p-5 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Smart Budget</h3>
              <p className="text-xs text-slate-400">Personalized allocation</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {status.label}
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>

        {/* Summary row */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">After bills & commitments:</span>
          <span className={`font-semibold ${allocation.remaining >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            £{Math.abs(allocation.remaining).toFixed(0)} {allocation.remaining >= 0 ? 'left' : 'short'}
          </span>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-700 p-5 space-y-5 animate-in slide-in-from-top-2 duration-200">
          {/* Allocation breakdown */}
          <div className="space-y-3">
            <h4 className="text-xs font-medium uppercase tracking-wider text-slate-400">Monthly Breakdown</h4>
            
            {/* Income */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span className="text-sm text-slate-300">Income</span>
              </div>
              <span className="text-sm font-medium text-emerald-400">
                £{allocation.monthly_income.toFixed(0)}
              </span>
            </div>
            
            {/* Essentials */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-slate-300">Essentials</span>
                <span className="text-xs text-slate-500">{essentialsPercent}%</span>
              </div>
              <span className="text-sm font-medium text-slate-200">
                £{allocation.essentials.toFixed(0)}
              </span>
            </div>
            
            {/* Debt payments */}
            {allocation.debt_payments > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-slate-300">Debt Payments</span>
                  <span className="text-xs text-slate-500">{debtPercent}%</span>
                </div>
                <span className="text-sm font-medium text-slate-200">
                  £{allocation.debt_payments.toFixed(0)}
                </span>
              </div>
            )}
            
            {/* Lifestyle */}
            {allocation.lifestyle > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-slate-300">Lifestyle</span>
                  <span className="text-xs text-slate-500">{lifestylePercent}%</span>
                </div>
                <span className="text-sm font-medium text-slate-200">
                  £{allocation.lifestyle.toFixed(0)}
                </span>
              </div>
            )}
            
            {/* Remaining */}
            <div className="pt-2 border-t border-slate-700 flex items-center justify-between">
              <span className="text-sm font-medium text-white">Remaining</span>
              <span className={`text-lg font-bold ${allocation.remaining >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                £{Math.abs(allocation.remaining).toFixed(0)}
              </span>
            </div>
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium uppercase tracking-wider text-slate-400">Recommendations</h4>
              
              {recommendations.slice(0, 2).map((rec, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-xl ${
                    rec.type === 'alert' ? 'bg-red-500/10 border border-red-500/20' :
                    rec.type === 'action_required' ? 'bg-amber-500/10 border border-amber-500/20' :
                    'bg-emerald-500/10 border border-emerald-500/20'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {rec.action === 'increase_debt_payment' && <CreditCard className="w-4 h-4 text-purple-400 mt-0.5" />}
                    {rec.action === 'save' && <PiggyBank className="w-4 h-4 text-blue-400 mt-0.5" />}
                    {rec.action === 'review_bills' && <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />}
                    {rec.action === 'reduce_lifestyle' && <TrendingUp className="w-4 h-4 text-amber-400 mt-0.5" />}
                    {rec.action === 'add_income' && <Wallet className="w-4 h-4 text-blue-400 mt-0.5" />}
                    
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{rec.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{rec.message}</p>
                      
                      {rec.amount && (
                        <div className="mt-2 text-sm font-semibold text-emerald-400">
                          +£{rec.amount.toFixed(0)}/month
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Suggested allocation bar */}
          {allocation.recommended_extra_debt > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wider text-slate-400">Suggested Allocation</h4>
              <div className="h-3 rounded-full bg-slate-700 overflow-hidden flex">
                <div 
                  className="bg-purple-500 h-full" 
                  style={{ width: `${(allocation.recommended_extra_debt / allocation.remaining * 100).toFixed(0)}%` }}
                  title="Extra to debt"
                />
                <div 
                  className="bg-blue-500 h-full" 
                  style={{ width: `${(allocation.recommended_savings / allocation.remaining * 100).toFixed(0)}%` }}
                  title="Savings"
                />
                <div 
                  className="bg-amber-500 h-full flex-1" 
                  title="Flexible spending"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  Debt
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  Savings
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  Flex
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
